import { Grid } from "@mui/material";
import { useActiveShopGridByShop, useShopGridCreate, useShopGridUpdate } from "queries/shop_grid";
import { Api } from "queries/types";
import useShopGridController from "modules/shop_grid/useShopGridController";
import ShopGridView from "modules/shop_grid/ShopGridView";
import FurniturePicker from "./FurniturePicker";
import GridSizeForm from "./GridSizeForm";
import Paper from "./Paper";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  activeGrid: Api.ShopGrid;
  onFurniturePickerToggle: (open: boolean) => void;
  shop: Api.Shop;
}

const FurnitureConfigurationStep = ({ activeGrid, shop, onFurniturePickerToggle }: Props) => {
  const { grid, configureCells, selectedCells, unselectAll, toggleCell } = useShopGridController(activeGrid, { isFurniture: true });

  const displayFurniturePicker = selectedCells.length > 0;

  useEffect(() => {
    onFurniturePickerToggle(displayFurniturePicker);
  }, [displayFurniturePicker, onFurniturePickerToggle]);

  return (
    <>
      <Grid item sx={{ mt: 2, display: { xs: "none", sm: "block" } }}>
        {grid.lines.length > 0 && (
          <ShopGridView grid={grid} selectedCells={selectedCells} toggleCell={toggleCell} mode="furniture" />
        )}
      </Grid>
      {displayFurniturePicker && (
        <FurniturePicker
          cells={selectedCells}
          onSubmit={configureCells}
          unselectAll={unselectAll}
          close={unselectAll}
          brandId={shop.brandId}
        />
      )}
    </>
  );
};

const Step3 = ({ shop }: { shop: Api.Shop }) => {
  const { data: activeGrid, isLoading} = useActiveShopGridByShop(shop);
  const [furniturePickerOpen, setFurniturePickerOpen] = useState(false);
  const createMutation = useShopGridCreate(shop);
  const updateMutation = useShopGridUpdate();
  const { t } = useTranslation();

  if (isLoading) return null;

  const paperSx = furniturePickerOpen ? { left: "0", width: "calc(100% - 450px)" } : { left: "5%", width: "90%" };

  return (
    <>
      <Paper sx={{ position: "absolute", transition: "left 100ms", ...paperSx }}>
        <GridSizeForm
          shopGrid={activeGrid || null}
          onSubmit={async ({ height, width }: { height: number; width: number }) => {
            if (activeGrid) {
              await updateMutation.mutateAsync({ id: activeGrid.id, height, width, shopGridCells: [] });
              window.location.reload();
            } else createMutation.mutate({ height, width, isActive: true, name: t('shop.inventory.defaultName'), shop: shop.erpId });
          }}
        />
        {activeGrid && (
          <FurnitureConfigurationStep
            activeGrid={activeGrid}
            shop={shop}
            onFurniturePickerToggle={(open: boolean) => setFurniturePickerOpen(open)}
          />
        )}
      </Paper>
    </>
  );
};

export default Step3;
